import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { setSentryUser } from "@/lib/sentry";
import type { User, Session } from "@supabase/supabase-js";

const IS_DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

const STORAGE_KEY = "dev-auth-user";

function makeDevUser(email: string, name: string): User {
  const id = deterministicId(email);
  return {
    id,
    email,
    user_metadata: { name },
    app_metadata: {},
    aud: "authenticated",
    created_at: new Date().toISOString(),
  } as unknown as User;
}

function makeDevSession(user: User): Session {
  return {
    access_token: `dev-token-${user.id}`,
    token_type: "bearer",
    expires_in: 99999,
    refresh_token: "dev-refresh",
    user,
  } as unknown as Session;
}

function deterministicId(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  }
  const hex = hash.toString(16).padStart(8, "0");
  return `00000000-0000-0000-0000-${hex.padStart(12, "0")}`;
}

function loadPersistedDevUser(): { user: User; session: Session } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { email, name } = JSON.parse(raw);
    const user = makeDevUser(email, name);
    return { user, session: makeDevSession(user) };
  } catch {
    return null;
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const persisted = IS_DEV_MODE ? loadPersistedDevUser() : null;
  const [user, setUser] = useState<User | null>(persisted?.user ?? null);
  const [session, setSession] = useState<Session | null>(
    persisted?.session ?? null,
  );
  const [loading, setLoading] = useState(!IS_DEV_MODE);

  useEffect(() => {
    if (IS_DEV_MODE) return;

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signIn(email: string, _password: string) {
    if (IS_DEV_MODE) {
      const name = email.split("@")[0] ?? "Usuário";
      const devUser = makeDevUser(email, name);
      const devSession = makeDevSession(devUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, name }));
      setUser(devUser);
      setSession(devSession);
      setSentryUser({ id: devUser.id, email: devUser.email });
      return;
    }
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: _password,
    });
    if (error) throw error;
  }

  async function signUp(email: string, _password: string, name: string) {
    if (IS_DEV_MODE) {
      const devUser = makeDevUser(email, name);
      const devSession = makeDevSession(devUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, name }));
      setUser(devUser);
      setSession(devSession);
      setSentryUser({ id: devUser.id, email: devUser.email });
      return;
    }
    const { error } = await supabase.auth.signUp({
      email,
      password: _password,
      options: { data: { name } },
    });
    if (error) throw error;
  }

  async function signOut() {
    if (IS_DEV_MODE) {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      setSession(null);
      setSentryUser(null);
      return;
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signIn, signUp, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
